// core/lib/cloze.js — CZ·1: cloze-deletion parsing + sibling expansion (pure). Anki's
// {{c1::text}} / {{c1::text::hint}} FORMAT is the reference (format only — no code); a source
// card whose front carries cloze markers expands to N siblings (one per distinct index) via the
// clozeOf/clozeIndex pattern, mirroring reversalOf: a sibling is an ordinary card record.
//
// Render rule (consumed by the girls' review surfaces BEFORE the escape-first markdown subset —
// the renderer itself stays cloze-blind and its escaping discipline unchanged):
//   front (unrevealed): the ACTIVE index renders as [...] (or [hint]); other indices render
//   their text plainly. back (revealed): the active answer emphasized (**bold** through the
//   existing subset), other indices plain.

const CLOZE_RE = /\{\{c(\d+)::((?:(?!\{\{|\}\}).)*?)\}\}/gs;

/** Does this text carry any cloze marker at all? Cheap pre-check for save paths. */
export function isCloze(text) {
  return /\{\{c\d+::/.test(String(text ?? ""));
}

/**
 * Parse cloze markers. Honest reject (ok:false + reason) on malformed input: an opener without a
 * closer, an empty deletion, index 0, or nesting — never a silent best-guess.
 * @returns {{ ok: true, indices: number[], segments: Array<{type:"text",text:string}|{type:"cloze",index:number,text:string,hint:string|null}> } | { ok: false, reason: string }}
 */
export function parseCloze(raw) {
  const text = String(raw ?? "");
  const segments = [];
  const indices = new Set();
  let last = 0;
  CLOZE_RE.lastIndex = 0;
  let m;
  while ((m = CLOZE_RE.exec(text)) !== null) {
    const index = Number(m[1]);
    const body = m[2];
    if (index < 1) return { ok: false, reason: `cloze index must start at 1 (saw c${m[1]})` };
    if (body.includes("{{")) return { ok: false, reason: "nested cloze markers are not supported" };
    const sep = body.indexOf("::");
    const answer = sep === -1 ? body : body.slice(0, sep);
    const hint = sep === -1 ? null : body.slice(sep + 2);
    if (!answer.trim()) return { ok: false, reason: `cloze c${index} has an empty deletion` };
    if (m.index > last) segments.push({ type: "text", text: text.slice(last, m.index) });
    segments.push({ type: "cloze", index, text: answer, hint: hint && hint.trim() ? hint.trim() : null });
    indices.add(index);
    last = CLOZE_RE.lastIndex;
  }
  const tail = text.slice(last);
  if (tail) segments.push({ type: "text", text: tail });
  // any opener left in PLAIN text is an unclosed or nested marker — reject, never best-guess
  if (segments.some((s) => s.type === "text" && /\{\{c\d+::/.test(s.text))) {
    return { ok: false, reason: "an unclosed or nested {{c…}} marker" };
  }
  if (indices.size === 0) return { ok: false, reason: "no cloze markers found" };
  return { ok: true, indices: [...indices].sort((a, b) => a - b), segments };
}

/** The blanked front for one sibling: active index → [...] / [hint]; others → plain text. */
export function clozeFront(raw, activeIndex) {
  const p = parseCloze(raw);
  if (!p.ok) return String(raw ?? ""); // a malformed source renders as-is — never a crash
  return p.segments.map((s) => s.type === "text" ? s.text
    : s.index === activeIndex ? `[${s.hint ?? "..."}]` : s.text).join("");
}

/** The revealed back for one sibling: the active answer emphasized via the markdown subset. */
export function clozeBack(raw, activeIndex) {
  const p = parseCloze(raw);
  if (!p.ok) return String(raw ?? "");
  return p.segments.map((s) => s.type === "text" ? s.text
    : s.index === activeIndex ? `**${s.text}**` : s.text).join("");
}

/**
 * CZ·5 — belt-and-braces render guard: blank EVERY deletion, so a cloze SOURCE that somehow reaches
 * a review surface never shows raw markers or leaks answers. A final regex safety strip catches even
 * a malformed source. (The store excludes sources from every queue; this is defense in depth.)
 */
export function clozeAllBlanks(raw) {
  const p = parseCloze(raw);
  if (p.ok) return p.segments.map((s) => s.type === "text" ? s.text : `[${s.hint ?? "..."}]`).join("");
  return String(raw ?? "").replace(/\{\{c\d+::.*?\}\}/gs, "[...]"); // never render raw markers
}

// CZ·6 — the ONE card-face pair every surface renders through (extracted from the per-app inline
// helpers that had already drifted). Ordinary cards pass through BYTE-IDENTICAL (`base` is the
// caller's chosen phrasing variant). A cloze SIBLING blanks its OWN index / reveals the filled
// sentence with its answer emphasized. A stray SOURCE (no clozeIndex) falls to the last-resort
// blank guard — a sibling NEVER does. Pure string in/out (the caller wraps in the markdown subset).
export function cardFront(card, base) {
  if (card.clozeIndex) return clozeFront(card.front, card.clozeIndex);
  if (isCloze(card.front)) return clozeAllBlanks(card.front); // stray source only
  return base;
}
export function cardBack(card) {
  if (card.clozeIndex) return clozeBack(card.front, card.clozeIndex) + (card.back && card.back.trim() ? "\n\n" + card.back : "");
  if (isCloze(card.front)) return ""; // a stray source has no answer to reveal
  return card.back;
}

/**
 * Expand a cloze SOURCE card into sibling payloads (no ids — the caller assigns via its own
 * newId path). One sibling per distinct index; the sibling's front is the RAW cloze text (the
 * surfaces render it per clozeIndex); back carries the source's back (extra context, often "").
 * A non-cloze or malformed front expands to [] — the caller treats the card as ordinary.
 * @param {{ id: string, deckId?: string, front: string, back?: string, tags?: string[] }} card
 */
export function expandCloze(card) {
  const p = parseCloze(card?.front);
  if (!p.ok) return [];
  return p.indices.map((index) => ({
    deckId: card.deckId,
    front: card.front,
    back: card.back ?? "",
    tags: Array.isArray(card.tags) ? [...card.tags] : [],
    clozeOf: card.id,
    clozeIndex: index,
  }));
}
